import React, { useState } from 'react';
import { Play, CheckCircle, ChevronRight, Clock, Shield, FileText, BarChart3, Calendar, Users } from 'lucide-react';
import { Logo } from '../components/Logo';

const FEATURES = [
  {
    id: 'onboarding',
    title: 'Guided Onboarding',
    description: 'From signup to your first framework in under 5 minutes',
    icon: CheckCircle,
    duration: '2 min',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    id: 'frameworks',
    title: 'Framework Dashboard',
    description: 'See your compliance status at a glance with our command center',
    icon: Shield,
    duration: '3 min',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'evidence',
    title: 'Auto-Evidence Capture',
    description: 'Upload policies once, watch evidence propagate everywhere',
    icon: FileText,
    duration: '2 min',
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'risk',
    title: 'Risk Management',
    description: 'AI-powered risk assessment and treatment plans',
    icon: BarChart3,
    duration: '2 min',
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'qms',
    title: 'QMS & Documents',
    description: 'Policy builder, document control, version history',
    icon: FileText,
    duration: '2 min',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'apps',
    title: 'Client & Exec Apps',
    description: 'Share trust pages and executive dashboards instantly',
    icon: Users,
    duration: '1 min',
    color: 'from-teal-500 to-emerald-500'
  }
];

const FAQ_ITEMS = [
  {
    question: 'How long does it take to get audit-ready?',
    answer: 'Most customers are audit-ready within 6-8 weeks, compared to 6-12 months with traditional approaches. Our AI pre-populates evidence and maps your existing policies automatically.'
  },
  {
    question: 'Do I need to be technical to use Simplif-i?',
    answer: 'Not at all. Our guided wizard walks you through everything step by step. If you can use email, you can use Simplif-i.'
  },
  {
    question: 'What frameworks do you support?',
    answer: 'ISO 27001, SOC 2, UK GDPR, Cyber Essentials, NIST CSF, and more. We also support custom frameworks for industry-specific requirements.'
  },
  {
    question: 'Can I try before I buy?',
    answer: 'Absolutely. Start with our free tier to explore the platform, or book a demo for a personalized walkthrough with our team.'
  },
  {
    question: 'What happens to my existing compliance work?',
    answer: 'Nothing is wasted. Upload your existing policies and documents - our AI will automatically map them to framework controls and identify gaps.'
  }
];

const PlatformWalkthroughPage = () => {
  const [activeFeature, setActiveFeature] = useState('onboarding');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Video embed URL - use local video file
  const VIDEO_URL = "/videos/platform_walkthrough.mp4";
  const POSTER_URL = "/videos/poster.jpg";
  
  // Links for CTAs
  const QUESTIONNAIRE_URL = "/questionnaire/enterprise";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl sticky top-0 z-50 bg-slate-900/80">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo className="h-10" />
          <a 
            href="/public" 
            className="px-4 py-2 rounded-lg bg-primary text-slate-900 font-medium hover:bg-primary/90 transition-colors"
          >
            Get Started Free
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            See How Simple Compliance Can Be
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            From zero to audit-ready in weeks, not months. Watch how Simplif-i transforms your compliance workflow.
          </p>
        </div>

        {/* Main Video Section */}
        <div className="mb-20">
          <div className="relative rounded-2xl overflow-hidden bg-slate-800/50 border border-slate-700/50 aspect-video">
            <video
              src={VIDEO_URL}
              className="absolute inset-0 w-full h-full object-contain bg-slate-900"
              controls
              playsInline
              preload="auto"
              poster={POSTER_URL}
              title="Platform Walkthrough"
            >
              <source src={VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-center text-slate-400 text-sm mt-4">
            Click play to watch the 30-second platform overview
          </p>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              No login required to watch
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Under 2 minutes
            </span>
          </div>
        </div>

        {/* Feature Showcase Grid */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Explore Key Features
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              const isActive = activeFeature === feature.id;
              
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveFeature(feature.id)}
                  className={`
                    p-6 rounded-xl text-left transition-all duration-300
                    ${isActive 
                      ? 'bg-gradient-to-br ' + feature.color + ' shadow-lg scale-[1.02]' 
                      : 'bg-slate-800/50 border border-slate-700/50 hover:border-slate-600'
                    }
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className={`
                      p-3 rounded-lg 
                      ${isActive ? 'bg-white/20' : 'bg-slate-700/50'}
                    `}>
                      <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold mb-1 ${isActive ? 'text-white' : 'text-slate-200'}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                        {feature.description}
                      </p>
                      <div className={`flex items-center gap-1 mt-2 text-xs ${isActive ? 'text-white/60' : 'text-slate-500'}`}>
                        <Clock className="w-3 h-3" />
                        <span>{feature.duration}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20">
          <h2 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQ_ITEMS.map((item, idx) => (
              <div 
                key={idx}
                className="rounded-xl bg-slate-800/50 border border-slate-700/50 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-slate-800/80 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{item.question}</span>
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-slate-400 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-br from-primary/20 to-emerald-500/20 border border-primary/30">
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to simplify your compliance?
          </h2>
          <p className="text-slate-400 mb-6 max-w-lg mx-auto">
            Join hundreds of companies who've cut their audit prep time by 80%.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/public" 
              className="px-6 py-3 rounded-xl bg-primary text-slate-900 font-semibold hover:bg-primary/90 transition-colors"
            >
              Start Free Trial
            </a>
            <a 
              href="/public#enterprise" 
              className="px-6 py-3 rounded-xl bg-slate-800 text-white font-semibold hover:bg-slate-700 transition-colors border border-slate-700"
            >
              Book Enterprise Demo
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-20 py-8 text-center text-sm text-slate-500">
        <p>© {new Date().getFullYear()} Simplif-i Limited. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PlatformWalkthroughPage;
